import { AdminAction } from '@/app/models/AdminAction';
import { type AppAbility } from '@/app/casl/ability';
import {
  AUTOMATIONS_PAGE_URL,
  BANK_ACCOUNTS_PAGE_URL,
  BANKS_PAGE_URL,
  BILLS_TO_PAY_TAGS_PAGE_URL,
  BILLS_TO_RECEIVE_TAGS_PAGE_URL,
  BUY_ORDERS_SETTINGS_PAGE_URL,
  BUY_ORDERS_TAGS_PAGE_URL,
  CASH_TAGS_PAGE_URL,
  CEST_CODES_PAGE_URL,
  CFOP_CODES_PAGE_URL,
  COMPANY_LIST_PAGE_URL,
  DIGITAL_CERTIFICATE_SETTINGS_PAGE_URL,
  FILE_UPLOAD_PAGE_URL,
  FINANCIAL_ACCOUNTS_PAGE_URL,
  INCOME_OUTCOME_CATEGORIES_PAGE_URL,
  INCOME_TAX_RECEIPT_TAGS_PAGE_URL,
  INVENTORY_DEPOSITS_PAGE_URL,
  INVENTORY_SETTINGS_PAGE_URL,
  NCM_CODES_PAGE_URL,
  NFE_SETTINGS_PAGE_URL,
  OPERATIONS_ORIGIN_PAGE_URL,
  OUTCOME_TAX_RECEIPT_TAGS_PAGE_URL,
  ACQUIRERS_PAGE_URL,
  PAYMENT_METHODS_PAGE_URL,
  PREFERENCES_CONVERSION_UNIT_PAGE_URL,
  PRODUCTS_BRANDS_PAGE_URL,
  PRODUCTS_TAGS_PAGE_URL,
  RECEIVE_METHODS_PAGE_URL,
  SALES_REQUESTS_SETTINGS_PAGE_URL,
  SALES_RISK_ANALYSIS_SETTINGS_PAGE_URL,
  SALES_TAGS_PAGE_URL,
  SEPARATION_TAGS_PAGE_URL,
  TAG_TEMPLATE_LIST_PAGE_URL,
  TAX_OPERATIONS_ORIGIN_OUT_PAGE_URL,
  TAX_SCENARIO_SETTINGS_PAGE_URL,
  USER_INTERFACE_PAGE_URL,
  USER_ROLES_LIST_PAGE_URL,
  WEBHOOK_EVENTS_PAGE_URL,
} from '@/api/core/links';
import { type Resource, RESOURCES } from '@/app/casl/resources';

export type SystemPreferenceOption = {
  name: string;
  description?: string;
  endpoint: string;
  pageTitle?: string;
  permissions?: Resource[];
};

export type SystemPreferenceSection = {
  name: string;
  description?: string;
  options: SystemPreferenceOption[];
  permissions?: Resource[];
};

export type SystemPreference = {
  name: string;
  description?: string;
  sections: SystemPreferenceSection[];
  permission?: Resource;
};

export const systemPreferences: SystemPreference[] = [
  {
    name: 'Geral',
    description: 'Geral',
    sections: [
      {
        name: '',
        options: [
          {
            name: 'Empresas',
            description: 'Cadastre as empresas (CNPJ) do ambiente, com regime, endereço, contatos e contador',
            endpoint: COMPANY_LIST_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_DADOS_DA_EMPRESA],
          },
          {
            name: 'Grupos de Permissão',
            description: 'Gerencie grupos de usuários e defina permissões de acesso ao sistema',
            endpoint: USER_ROLES_LIST_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_PERMISSOES_DE_USUARIOS],
          },
          {
            name: 'Configurações do envio de arquivos',
            description: 'Configure limites de tamanho e tipos de arquivos permitidos para upload',
            endpoint: FILE_UPLOAD_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_ENVIO_ARQUIVOS],
          },
          {
            name: 'Provedores de comunicação',
            description: 'Configure os serviços usados para enviar mensagens',
            endpoint: '/preferencias/provedor-comunicacao',
            permissions: [RESOURCES.PREFERENCIAS_PROVEDORES_COMUNICACAO],
          },
          {
            name: 'Canais de comunicação',
            description: 'Escolha o provedor padrão de cada canal de comunicação',
            endpoint: '/preferencias/comunicacao',
            permissions: [RESOURCES.PREFERENCIAS_CANAIS_COMUNICACAO],
          },
          {
            name: 'Configurações da Interface de usuário',
            description: 'Personalize a aparência e comportamento da interface do sistema',
            endpoint: USER_INTERFACE_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_INTERFACE],
          },
        ],
      },
    ],
  },
  {
    name: 'Cadastros',
    description: 'Cadastros',
    sections: [
      {
        name: '',
        options: [
          {
            name: 'Configurações do certificado digital',
            description: 'Configure o certificado digital A1 para emissão de notas fiscais eletrônicas',
            endpoint: DIGITAL_CERTIFICATE_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CERTIFICADO_DIGITAL],
          },
          {
            name: 'Marcadores de produto',
            description: 'Crie e gerencie etiquetas para categorizar e organizar produtos',
            endpoint: PRODUCTS_TAGS_PAGE_URL,
            permissions: [RESOURCES.CATALOGO_MARCADORES_PRODUTOS],
          },
          {
            name: 'Marcas de produto',
            description: 'Cadastre e gerencie as marcas dos produtos comercializados',
            endpoint: PRODUCTS_BRANDS_PAGE_URL,
            permissions: [RESOURCES.CATALOGO_MARCAS],
          },
          {
            name: 'Unidades de conversão',
            description: 'Configure unidades de medida e suas conversões (kg, g, L, ml, etc.)',
            endpoint: PREFERENCES_CONVERSION_UNIT_PAGE_URL,
            permissions: [RESOURCES.CATALOGO_UNIDADES_CONVERSAO],
          },
          {
            name: 'Templates de etiqueta',
            description: 'Crie e gerencie modelos de etiquetas para impressão',
            endpoint: TAG_TEMPLATE_LIST_PAGE_URL,
            permissions: [RESOURCES.CATALOGO_TEMPLATES_ETIQUETA],
          },
        ],
      },
      {
        name: 'Fiscal',
        options: [
          {
            name: 'Cenários fiscais',
            description: 'Configure cenários fiscais para produtos',
            endpoint: TAX_SCENARIO_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CENARIOS_FISCAIS],
          },
          {
            name: 'Configuração da nota fiscal eletrônica (NFe)',
            description: 'Configure parâmetros para emissão de NFe, como série, numeração e ambiente',
            endpoint: NFE_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_EMISSAO_FISCAL],
          },
          {
            name: 'Códigos CFOP',
            description: 'Gerencie Códigos Fiscais de Operações e Prestações para operações fiscais',
            endpoint: CFOP_CODES_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CFOP],
          },
          {
            name: 'Códigos CEST',
            description: 'Cadastre Códigos Especificadores da Substituição Tributária',
            endpoint: CEST_CODES_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CEST],
          },
          {
            name: 'Códigos NCM',
            description: 'Gerencie a Nomenclatura Comum do Mercosul para classificação de produtos',
            endpoint: NCM_CODES_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_NCM],
          },
        ],
      },
    ],
  },
  {
    name: 'Suprimentos',
    description: 'Suprimentos',
    sections: [
      {
        name: '',
        options: [
          {
            name: 'Configurações de estoque',
            description: 'Configure parâmetros gerais de controle de estoque e inventário',
            endpoint: INVENTORY_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_ESTOQUE],
          },
          {
            name: 'Configurações de ordens de compra',
            description: 'Defina comportamentos padrão para criação e gestão de ordens de compra',
            endpoint: BUY_ORDERS_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_ORDENS_COMPRA],
          },
          {
            name: 'Automações',
            description: 'Gere ordens de compra e picklists automaticamente, nos dias e na hora escolhidos',
            endpoint: AUTOMATIONS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_REPOSICAO_AUTOMATICA, RESOURCES.PREFERENCIAS_PICKLIST_AUTOMATICA],
          },
          {
            name: 'Estoques centrais',
            description: 'Cadastre e gerencie o estoque central de produtos',
            endpoint: INVENTORY_DEPOSITS_PAGE_URL,
            permissions: [RESOURCES.SUPRIMENTOS_DEPOSITOS],
          },
          {
            name: 'Marcadores nas ordens de compra',
            description: 'Crie etiquetas para categorizar e filtrar ordens de compra',
            endpoint: BUY_ORDERS_TAGS_PAGE_URL,
            permissions: [RESOURCES.SUPRIMENTOS_MARCADORES_ORDENS_COMPRA],
          },
        ],
      },
      {
        name: 'Notas Fiscais',
        options: [
          {
            name: 'Marcadores nas notas fiscais de entrada',
            description: 'Crie etiquetas para organizar e classificar notas fiscais de entrada',
            endpoint: INCOME_TAX_RECEIPT_TAGS_PAGE_URL,
            permissions: [RESOURCES.SUPRIMENTOS_MARCADORES_NOTAS_ENTRADA],
          },
          {
            name: 'Naturezas de operação (entrada)',
            description: 'Configure CFOP e regras fiscais para operações de entrada de mercadorias',
            endpoint: OPERATIONS_ORIGIN_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_NATUREZAS_OPERACAO_ENTRADA],
          },
        ],
      },
    ],
  },
  {
    name: 'Vendas',
    description: 'Vendas',
    sections: [
      {
        name: '',
        options: [
          {
            name: 'Configurações dos pedidos de venda',
            description: 'Defina comportamentos padrão para criação e gestão de pedidos de venda',
            endpoint: SALES_REQUESTS_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_PEDIDOS_VENDA],
          },
          {
            name: 'Marcadores nas vendas',
            description: 'Crie etiquetas para categorizar e filtrar vendas realizadas',
            endpoint: SALES_TAGS_PAGE_URL,
            permissions: [RESOURCES.VENDAS_MARCADORES_PEDIDOS],
          },
          {
            name: 'Análise de risco de venda',
            description: 'Pontos, limites e horários de risco da análise nas vendas de autoatendimento',
            endpoint: SALES_RISK_ANALYSIS_SETTINGS_PAGE_URL,
            permissions: [RESOURCES.ANALISE_DE_RISCO],
          },
        ],
      },
      {
        name: 'Notas Fiscais',
        options: [
          {
            name: 'Marcadores nas notas fiscais de saída',
            description: 'Crie etiquetas para organizar e classificar notas fiscais de saída',
            endpoint: OUTCOME_TAX_RECEIPT_TAGS_PAGE_URL,
            permissions: [RESOURCES.VENDAS_MARCADORES_NOTAS_FISCAIS],
          },
          {
            name: 'Naturezas de operação (saída)',
            description: 'Configure CFOP e regras fiscais para operações de saída de mercadorias',
            endpoint: TAX_OPERATIONS_ORIGIN_OUT_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_NATUREZAS_OPERACAO_SAIDA],
          },
        ],
      },
      {
        name: 'Separação e abastecimento',
        options: [
          {
            name: 'Configurações da separação',
            description: 'Defina o comportamento das operações de separação',
            endpoint: '/preferencias/separacao',
            permissions: [RESOURCES.PREFERENCIAS_SEPARACAO],
          },
          {
            name: 'Marcadores na separação',
            description: 'Crie etiquetas para organizar e priorizar processos de separação de pedidos',
            endpoint: SEPARATION_TAGS_PAGE_URL,
            permissions: [RESOURCES.SUPRIMENTOS_MARCADORES_PICKING],
          },
        ],
      },
    ],
  },
  {
    name: 'Finanças',
    description: 'Finanças',
    sections: [
      {
        name: '',
        options: [
          {
            name: 'Bancos',
            description: 'Cadastre instituições bancárias para uso no sistema financeiro',
            endpoint: BANKS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_BANCOS],
          },
          {
            name: 'Categorias financeiras',
            description: 'Organize receitas e despesas em categorias para controle financeiro',
            endpoint: INCOME_OUTCOME_CATEGORIES_PAGE_URL,
            permissions: [RESOURCES.FINANCEIRO_PLANO_DE_CONTAS],
          },
          {
            name: 'Formas de pagamento',
            description: 'Configure métodos de pagamento aceitos (dinheiro, cartão, PIX, etc.)',
            endpoint: PAYMENT_METHODS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_FORMAS_PAGAMENTO],
          },
          {
            name: 'Formas de recebimento',
            description: 'Configure métodos de recebimento de valores dos clientes',
            endpoint: RECEIVE_METHODS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_FORMAS_RECEBIMENTO],
          },
          {
            name: 'Contas bancárias',
            description: 'Cadastre e gerencie contas bancárias da empresa para conciliação',
            endpoint: BANK_ACCOUNTS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CONTAS_BANCARIAS],
          },
          {
            name: 'Adquirentes',
            description: 'Cadastre adquirentes e suas integrações de pagamento',
            endpoint: ACQUIRERS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_GATEWAYS_PAGAMENTO],
          },
          {
            name: 'Contas financeiras',
            description: 'Gerencie contas para controle de fluxo de caixa e movimentações',
            endpoint: FINANCIAL_ACCOUNTS_PAGE_URL,
            permissions: [RESOURCES.PREFERENCIAS_CONTAS_FINANCEIRAS],
          },
          {
            name: 'Marcadores no caixa',
            description: 'Crie etiquetas para categorizar e organizar movimentações de caixa',
            endpoint: CASH_TAGS_PAGE_URL,
            permissions: [RESOURCES.FINANCEIRO_MARCADORES_CAIXA],
          },
          {
            name: 'Marcadores nas contas a pagar',
            description: 'Crie etiquetas para organizar e priorizar contas a pagar',
            endpoint: BILLS_TO_PAY_TAGS_PAGE_URL,
            permissions: [RESOURCES.FINANCEIRO_MARCADORES_CONTAS_PAGAR],
          },
          {
            name: 'Marcadores nas contas a receber',
            description: 'Crie etiquetas para organizar e acompanhar contas a receber',
            endpoint: BILLS_TO_RECEIVE_TAGS_PAGE_URL,
            permissions: [RESOURCES.FINANCEIRO_MARCADORES_CONTAS_RECEBER],
          },
        ],
      },
      {
        name: 'Integrações',
        options: [
          {
            name: 'Eventos de Webhook',
            description: 'Visualize e acompanhe eventos de webhook recebidos das integrações de pagamento',
            endpoint: WEBHOOK_EVENTS_PAGE_URL,
            permissions: [RESOURCES.INTEGRACOES_EVENTOS_WEBHOOK],
          },
        ],
      },
    ],
  },
];

export function getAvailablePreferences(ability: AppAbility): SystemPreference[] {
  const availablePreferences: SystemPreference[] = [];
  for (const preference of systemPreferences) {
    const availableSections: SystemPreferenceSection[] = [];
    for (const section of preference.sections) {
      const availableOptions = section.options.filter((option) => {
        const resources = option.permissions ?? [];
        if (resources.length === 0) return true;
        return resources.some((resource) => ability.can(AdminAction.CONSULTAR, resource));
      });
      if (availableOptions.length === 0) continue;
      const availableSection = { ...section, options: availableOptions };
      availableSections.push(availableSection);
    }
    if (availableSections.length === 0) continue;
    const availablePreference = { ...preference, sections: availableSections };
    availablePreferences.push(availablePreference);
  }
  return availablePreferences;
}
